/**
 * Типы рассылок (серия N, пакет N-3-ui).
 *
 * Зеркало схем роутера broadcasts.py (Richat_next @77b9e05):
 * все эндпоинты под /assistants/{assistantId}/broadcasts/...
 *
 * ВАЖНО (следствия контракта бэка):
 * - Любая мутация (create/update/media) возвращает ЦЕЛИКОМ BroadcastResponse —
 *   поэтому фронт не собирает состояние сам, а кладёт ответ сервера в кэш.
 * - channel_id задаётся ТОЛЬКО при создании, в PATCH его нет: сменить канал
 *   у черновика нельзя (получатели привязаны к каналу). В UI выбор канала —
 *   шаг создания.
 * - Медиа удаляется ПО ИНДЕКСУ, после удаления индексы сдвигаются → список
 *   всегда перерисовываем из ответа сервера, не из локального стейта.
 * - Кнопки — только URL (text + url), callback-кнопок бэк не принимает.
 */

/** Платформы, куда возможна рассылка. Виджет исключён (нет постоянного chat_id). */
export type BroadcastPlatform = 'telegram' | 'max';

/**
 * Статусы рассылки. В N-3 создаётся только 'draft' —
 * остальные появятся с движком отправки (N-4).
 */
export type BroadcastStatus = 'draft' | 'queued' | 'sending' | 'done' | 'failed' | 'cancelled';

export interface BroadcastMediaItem {
  type: 'image' | 'video' | 'document';
  s3_key: string;
  size: number;
  mime: string;
  filename?: string | null;
}

export interface BroadcastButton {
  text: string;
  url: string;
}

export interface Broadcast {
  id: string;
  assistant_id: string;
  channel_id: string;
  platform: string;
  status: BroadcastStatus;
  title: string | null;
  body_text: string;
  media: BroadcastMediaItem[];
  buttons: BroadcastButton[];
  total_count: number;
  sent_count: number;
  delivered_count: number;
  failed_count: number;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface BroadcastListResponse {
  items: Broadcast[];
}

export interface BroadcastCreateRequest {
  channel_id: string;
  title?: string | null;
  body_text?: string;
}

export interface BroadcastUpdateRequest {
  title?: string | null;
  body_text?: string;
  buttons?: BroadcastButton[];
}

export interface RecipientsPreview {
  channel_id: string;
  platform: string;
  recipients_count: number;
}

/** Человеческие подписи статусов + цвет тега (палитра «Графит», без кислотных). */
export const BROADCAST_STATUS_META: Record<BroadcastStatus, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'default' },
  queued: { label: 'В очереди', color: 'processing' },
  sending: { label: 'Отправляется', color: 'processing' },
  done: { label: 'Завершена', color: 'success' },
  failed: { label: 'Ошибка', color: 'error' },
  cancelled: { label: 'Отменена', color: 'default' },
};

export const BROADCAST_PLATFORM_LABELS: Record<string, string> = {
  telegram: 'Telegram',
  max: 'MAX',
  widget: 'Виджет',
};

/** Лимиты медиа — те же, что валидирует бэк (broadcasts.py). Проверяем и здесь,
 *  чтобы не гонять 50 МБ на сервер ради 400-ки. */
export const MEDIA_LIMITS = {
  maxItems: 10,
  maxImageBytes: 10 * 1024 * 1024,
  maxVideoBytes: 50 * 1024 * 1024,
  imageMimes: ['image/jpeg', 'image/png', 'image/webp'],
  videoMimes: ['video/mp4', 'video/quicktime'],
  maxDocBytes: 50 * 1024 * 1024,
  docMimes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    'application/rtf',
    'text/plain',
    'text/csv',
    'application/zip',
    'application/x-rar-compressed',
    'application/vnd.rar',
    'application/x-7z-compressed',
    'application/json',
    'application/xml',
    'text/xml',
  ],
} as const;

/** Получатель, до которого не дошло (с причиной). */
export interface FailedRecipient {
  external_id: string;
  status: 'failed' | 'blocked';
  error: string | null;
}

/**
 * Статистика доставки рассылки (N-5-a).
 *
 * ⚠️ «Прочитано» здесь нет и не будет: мессенджеры не дают read-receipts для
 * исходящих сообщений бота. Честный максимум — доставлено / не доставлено /
 * заблокировали бота. Клики появятся отдельно (N-5-b, через URL-редирект).
 */
export interface BroadcastStats {
  broadcast_id: string;
  status: BroadcastStatus;
  total: number;
  sent: number;
  failed: number;
  blocked: number;
  pending: number;
  started_at: string | null;
  finished_at: string | null;
  duration_sec: number | null;
  failed_recipients: FailedRecipient[];
}
